// English (Canada) dictionary
export const enCA = {
  app: {
    title: "FBO Micro UI App",
    yes: "Yes",
    no: "No",
    theme: {
      light: "Light",
      dark: "Dark",
      system: "System",
      toggle: "Toggle theme",
    },
    settings: {
      open: "Open settings",
      title: "Application Settings",
      tabs: {
        auth: "Authentication",
        features: "Features",
        codes: "Code Values",
        debug: "Debug",
      },
    },
    api: {
      title: "API Configuration",
      url: "API URL:",
      environment: "Environment:",
      adminServiceApiEndpoint: "Admin Service API:",
    },
    auth: {
      title: "Authentication Status",
      status: "Status:",
      authenticated: "Authenticated",
      unauthenticated: "Unauthenticated",
      token: "Token:",
      source: "Source:",
      sourceUrl: "URL Parameter",
      sourceDefault: "Default Token",
      sourceCustom: "Custom Token",
      sourceParent: "Parent Window",
      sourceNone: "None",
      noTokenMessage:
        "No authentication token found. The application expects a token parameter in the URL or from the parent window.",
      clearCustomToken: "Clear custom token",
      confirmClearToken: "Are you sure you want to clear your custom token?",
      customToken: {
        title: "Custom Authentication Token",
        description: "Enter a custom token to use instead of the default token",
        placeholder: "Enter your JWT token here",
        save: "Save Token",
        paste: "Paste",
        success: "Token saved successfully!",
        error: "Please enter a valid token",
      },
    },
    apiExample: {
      title: "API Example",
      loading: "Loading...",
      callMainApi: "Call Main API",
      callAdminApi: "Call Admin API",
    },
    language: {
      switch: "Switch language",
      english: "English (Canada)",
      french: "Français (Canada)",
      current: "Current Language:",
      source: "Language Source:",
      sourceUrl: "URL Parameter",
      sourceCookie: "Cookie",
      sourceDefault: "Default",
    },
    loading: {
      general: "Loading...",
      initializing: "Initializing...",
      transactions: "Loading transactions..."
    }
  },
  counter: {
    decrease: "Decrease",
    increase: "Increase",
  },
  featureFlags: {
    title: "Feature Flags",
    enabled: "Enabled",
    disabled: "Disabled",
    noFlags: "No feature flags available",
    mockData: "Mock Data",
    mockDataTitle: "Using Mock Data",
    mockDataDescription: "Feature flags are currently loaded from mock data due to CORS restrictions.",
    showFlags: "Show Feature Flags",
    hideFlags: "Hide Feature Flags",
    errors: {
      title: "Error",
      loadFailed: "Failed to load feature flags. Please try again later.",
      notAuthenticated: "Authentication required to load feature flags",
    },
  },
  codeValues: {
    title: "Code Values",
    noValues: "No code values available",
    showValues: "Show Code Values",
    hideValues: "Hide Code Values",
    errors: {
      title: "Error",
      loadFailed: "Failed to load code values. Please try again later.",
      notAuthenticated: "Authentication required to load code values",
    },
  },  
  filters: {
    select: "Select...",
    selected: "selected",
    selectAll: "Select All",
    equals: "Equals",
    doesNotEqual: "Does not Equal",
    lessThan: "Less than",
    greaterThan: "Greater than",
    lessThanOrEqualTo: "Less than or equal to",
    greaterThanOrEqualTo: "Greater than or equal to",
    between: "Between",
    reset: "Reset"
  },
  pagination: {
    page: "Page",
    of: "of",
    items: "items"
  },
  dashboard: {
    title: "Commission Management Dashboard",
    cards: {
      pendingCommissions: {
        title: "Pending Commissions",
        transactions: "transactions"
      },
      permanentPlacements: {
        title: "Permanent Placements"
      },
      drawsAndAdjustments: {
        title: "Draws + Recurring Adjustments",
        transactions: "transactions"
      },
      lastPayout: {
        title: "Last Scheduled Payout",
        none: "None",
        processedOn: "Processed on"
      }
    },
    commissionSummary: {
      title: "Commissions Available for Payout",
      selectPeriod: "Select period",
      standardCommissions: "Standard Commissions",
      permanentCommissions: "Permanent Placement Commissions",
      total: "Total",
      openingBalance: "Opening Balance",
      adjustments: "Adjustments",
      draws: "Draws",
      closingBalance: "Closing Balance (Carried Forward)",
      totalPayable: "Total Payable",
      includesDrawsPayable: "Includes Draws Payable"
    },
    transactionTypes: {
      permanent: "Permanent",
      standard: "Standard",
      draw: "Draw",
      adjustment: "Adjustment"
    },
    transactions: {
      title: "Commission Transactions",
      description: "Manage commission transactions for this payout",
      tabs: {
        current: "Available for Payout",
        future: "Future Scheduled"
      },
      selectMessage: "Select transactions to include in the payout",
      columns: {
        employeeName: "Employee Name",
        transactionType: "Transaction Type",
        transactionDate: "Transaction Date",
        client: "Client",
        workerName: "Worker Name",
        transactionNumber: "Transaction Number",
        startDate: "Transaction Start Date",
        endDate: "Transaction End Date",
        grossMargin: "Gross Margin Amount",
        revenue: "Revenue Amount",
        commissionSplit: "Commission Split %",
        commission: "Commission %",
        currency: "Currency",
        commissionAmount: "Commission Amount",
        invoiceARStatus: "Invoice AR Status",
        permGuaranteeDate: "Perm Guarantee Date",
        scheduledPaymentDate: "Scheduled Payment Date",
        commissionRole: "Commission Role",
        commissionRateDescription: "Commission Description",
        billingTransactionStatus: "Billing Transaction Status",
        invoiceNumber: "Invoice Number",
        invoiceReleased: "Invoice Released",
        invoiceDate: "Invoice Date",
        clientPaymentDate: "Client Payment Date",
        tierLevel: "Tier Level",
        commissionAccrualDate: "Commission Accrual Date",
        internalCompany: "Internal Company",
        commissionAdjustmentDescription: "Commission Adjustment Description"
      },
      noTransactions: {
        current: "No transactions available for payout",
        future: "No future scheduled transactions found"
      }
    },
    actions: {
      preparePayout: "Prepare Payout",
      releaseCommission: "Release Commission",
      viewHistory: "View History",
      resetFilter: "Reset Filter",
      loadTransactions: "Load Transactions",
      actionsPlaceholder: "Actions...",
      generateDraws: "Generate Draws and Recurring Adjustments",
      generateDrawsConfirmTitle: "Generate Draws and Recurring Adjustments",
      generateDrawsConfirmDescription: "This will generate draw and recurring adjustment transactions for {period}. Do you want to proceed?",
      generateDrawsConfirmAction: "Proceed",
      generateDrawsConfirmCancel: "Cancel",
      proceed: "Proceed",
      deferToFuture: "Defer to Future",
      deferModalTitle: "Defer to Future",
      deferDateLabel: "Select a date",
      deferConfirm: "Confirm Defer",
      deferCancel: "Cancel",
      finalizePeriod: "Finalize Period",
      finalizing: "Finalizing...",
      finalizePeriodConfirmTitle: "Finalize Period",
      finalizePeriodConfirmDescription: "Are you sure you want to finalize this period? This action cannot be undone. Any late transactions will be moved to the next period.",
      finalizePeriodConfirmAction: "Finalize",
      finalizePeriodConfirmCancel: "Cancel",
      overdue: "Overdue"
    },
    messages: {
      balanceError: "Error",
      balanceErrorDescription: "Failed to load opening balances.",
      refreshError: "Error",
      refreshErrorDescription: "Failed to refresh transactions. Please try again.",
      releaseSuccess: "Success",
      releaseSuccessDescription: "Transaction(s) scheduled successfully.",
      releaseError: "Error",
      releaseErrorDescription: "Failed to schedule commissions. Please try again.",
      generateSuccess: "Success",
      generateSuccessDescription: "Successfully generated recurring adjustment transaction(s).",
      generateError: "Error",
      generateErrorDescription: "Failed to generate recurring adjustments. Please try again.",
      finalizeSuccess: "Success",
      finalizeSuccessDescription: "Period finalized successfully. Moving to next period.",
      finalizeError: "Error",
      finalizeErrorDescription: "Failed to finalize period.",
      payoutSuccess: "Payout Confirmed",
      payoutSuccessDescription: "user(s) payout confirmed successfully.",
      payoutError: "Error",
      payoutErrorDescription: "Failed to confirm payout. Please try again.",
      usersError: "Error",
      usersErrorDescription: "Failed to load commission users for adhoc dropdown.",
      deferSuccess: "Success",
      deferSuccessDescription: "Transaction(s) deferred successfully.",
      deferError: "Error",
      deferErrorDescription: "Failed to defer transactions. Please try again.",
      deferDateFuture: "Defer date must be in the future."
    }
  },
  prepare: {
    title: "Prepare Commission Payout",
    backToDashboard: "Back to Dashboard",
    loading: "Initializing...",
    noTransactions: {
      message: "No transactions selected. Please select transactions from the dashboard.",
      returnButton: "Return to Dashboard"
    },
    summary: {
      title: "Payout Summary",
      description: "Commission Payout",
      commissionSummary: {
        title: "Commission Summary",
        openingBalance: "Opening Balance",
        standardCommissions: "Standard Commissions",
        permanentPlacements: "Permanent Placement Commissions",
        draws: "Draws This Period",
        adjustments: "Adjustments This Period",
        netChange: "Net Change This Period",
        drawsPayable: "Draws Payable This Period",
        netPayable: "Net Payable This Period",
        closingBalance: "Closing Balance (to be carried forward)"
      },
      userBreakdown: {
        title: "User Breakdown",
        columns: {
          employeeName: "Employee Name",
          role: "Role",
          standardCommissions: "Standard Commissions",
          permanentPlacements: "Permanent Placements",
          totalAmount: "Commissions Earned",
          openingBalance: "Opening Balance",
          draws: "Draws",
          adjustments: "Adjustments",
          netChange: "Net Change",
          netPayable: "Net Payable",
          closingBalance: "Closing Balance"
        }
      }
    },
    payoutDetails: {
      title: "Payout Details",
      payoutType: "Payout Type",
      scheduledPayout: "Scheduled Payout",
      currentPeriodEnding: "Current Period Ending:",
      scheduledPayoutDate: "Scheduled Payout:",
      adhocPayout: "Adhoc Payout",
      statementPeriod: "Statement Period",
      selectEmployee: "Select Employee",
      selectEmployeePlaceholder: "Choose an employee...",
      payoutDate: "Payout Date",
      notes: "Notes (Optional)",
      notesPlaceholder: "Add any additional notes about this payout..."
    },
    actions: {
      confirm: "Confirm Payout",
      cancel: "Cancel",
      resetFilter: "Reset Filter"
    }
  },
  history: {
    title: "Commission Payout History",
    backToDashboard: "Back to Dashboard",
    loading: "Initializing...",
    payouts: {
      title: "Historical Payouts",
      description: "View and access previous commission payouts",
      selectYear: "Select Year",
      columns: {
        id: "Payout Batch Id",
        payoutDate: "Payout Date",
        processedDate: "Processed Date",
        numOfPayees: "# Payees",
        openingBalance: "Opening Balance",
        standard: "Standard",
        permanent: "Permanent",
        commissionsEarned: "Commissions Earned",
        draws: "Draws",
        adjustments: "Adjustments",
        netChange: "Net Change",
        netPayable: "Net Payable",
        closingBalance: "Closing Balance",
        notes: "Notes",
        payoutType: "Payout Type"
      },
      payoutTypes: {
        scheduled: "Scheduled",
        adhoc: "Adhoc"
      },
      loadingMessage: "Loading...",
      errorMessage: "Failed to fetch payout history"
    },
    actions: {
      resetFilter: "Reset Filter"
    }
  },
  summary: {
    title: "Commission Payout Summary",
    backToHistory: "Back to History"
  }
}

// French (Canada) dictionary
export const frCA = {
  app: {
    title: "Application Micro UI FBO",
    yes: "Oui",
    no: "Non",
    theme: {
      light: "Clair",
      dark: "Sombre",
      system: "Système",
      toggle: "Changer le thème",
    },
    settings: {
      open: "Ouvrir les paramètres",
      title: "Paramètres de l'Application",
      tabs: {
        auth: "Authentification",
        features: "Fonctionnalités",
        codes: "Valeurs de Code",
        debug: "Débogage",
      },
    },
    api: {
      title: "Configuration de l'API",
      url: "URL de l'API :",
      environment: "Environnement :",
      adminServiceApiEndpoint: "API de Service Admin :",
    },
    auth: {
      title: "État d'Authentification",
      status: "Statut :",
      authenticated: "Authentifié",
      unauthenticated: "Non Authentifié",
      token: "Jeton :",
      source: "Source :",
      sourceUrl: "Paramètre d'URL",
      sourceDefault: "Jeton par Défaut",
      sourceCustom: "Jeton Personnalisé",
      sourceParent: "Fenêtre Parent",
      sourceNone: "Aucune",
      noTokenMessage:
        "Aucun jeton d'authentification trouvé. L'application attend un paramètre de jeton dans l'URL ou de la fenêtre parent.",
      clearCustomToken: "Effacer le jeton personnalisé",
      confirmClearToken: "Êtes-vous sûr de vouloir effacer votre jeton personnalisé ?",
      customToken: {
        title: "Jeton d'Authentification Personnalisé",
        description: "Entrez un jeton personnalisé à utiliser au lieu du jeton par défaut",
        placeholder: "Entrez votre jeton JWT ici",
        save: "Enregistrer le Jeton",
        paste: "Coller",
        success: "Jeton enregistré avec succès !",
        error: "Veuillez entrer un jeton valide",
      },
    },
    apiExample: {
      title: "Exemple d'API",
      loading: "Chargement...",
      callMainApi: "Appeler l'API Principale",
      callAdminApi: "Appeler l'API Admin",
    },
    language: {
      switch: "Changer de langue",
      english: "English (Canada)",
      french: "Français (Canada)",
      current: "Langue Actuelle :",
      source: "Source de la Langue :",
      sourceUrl: "Paramètre d'URL",
      sourceCookie: "Cookie",
      sourceDefault: "Par Défaut",
    },
    loading: {
      general: "Chargement...",
      initializing: "Initialisation...",
      transactions: "Chargement des transactions..."
    }
  },
  counter: {
    decrease: "Diminuer",
    increase: "Augmenter",
  },
  featureFlags: {
    title: "Indicateurs de Fonctionnalités",
    enabled: "Activé",
    disabled: "Désactivé",
    noFlags: "Aucun indicateur de fonctionnalité disponible",
    mockData: "Données Simulées",
    mockDataTitle: "Utilisation de Données Simulées",
    mockDataDescription:
      "Les indicateurs de fonctionnalités sont actuellement chargés à partir de données simulées en raison des restrictions CORS.",
    showFlags: "Afficher les Indicateurs",
    hideFlags: "Masquer les Indicateurs",
    errors: {
      title: "Erreur",
      loadFailed: "Échec du chargement des indicateurs de fonctionnalités. Veuillez réessayer plus tard.",
      notAuthenticated: "Authentification requise pour charger les indicateurs de fonctionnalités",
    },
  },
  codeValues: {
    title: "Valeurs de Code",
    noValues: "Aucune valeur de code disponible",
    showValues: "Afficher les Valeurs de Code",
    hideValues: "Masquer les Valeurs de Code",
    errors: {
      title: "Erreur",
      loadFailed: "Échec du chargement des valeurs de code. Veuillez réessayer plus tard.",
      notAuthenticated: "Authentification requise pour charger les valeurs de code",
    },
  },  
  filters: {
    select: "Sélectionner...",
    selected: "sélectionné",
    selectAll: "Tout sélectionner",
    equals: "Égal à",
    doesNotEqual: "Différent de",
    lessThan: "Moins que",
    greaterThan: "Plus que",
    lessThanOrEqualTo: "Moins que ou égal à",
    greaterThanOrEqualTo: "Plus que ou égal à",
    between: "Entre",
    reset: "Réinitialiser"
  },
  pagination: {
    page: "Page",
    of: "de",
    items: "éléments"
  },
  dashboard: {
    title: "Tableau de Bord des Commissions",
    cards: {
      pendingCommissions: {
        title: "Commissions en Attente",
        transactions: "transactions"
      },
      permanentPlacements: {
        title: "Placements Permanents"
      },
      drawsAndAdjustments: {
        title: "Prélèvements + Ajustements Récurrents",
        transactions: "transactions"
      },
      lastPayout: {
        title: "Dernier Paiement Planifié",
        none: "Aucun",
        processedOn: "Traité le"
      }
    },
    commissionSummary: {
      title: "Commissions Disponibles pour le Paiement",
      selectPeriod: "Sélectionner la période",
      standardCommissions: "Commissions Standards",
      permanentCommissions: "Commissions de Placement Permanent",
      total: "Total",
      openingBalance: "Solde d'Ouverture",
      adjustments: "Ajustements",
      draws: "Prélèvements",
      closingBalance: "Solde de Clôture (Reporté)",
      totalPayable: "Total à Payer",
      includesDrawsPayable: "Comprend les Prélèvements à Payer"
    },
    transactionTypes: {
      permanent: "Permanent",
      standard: "Standard",
      draw: "Prélèvement",
      adjustment: "Ajustement"
    },
    transactions: {
      title: "Transactions de Commission",
      description: "Gérer les transactions de commission pour ce paiement",
      tabs: {
        current: "Disponible pour Paiement",
        future: "Planifiées"
      },
      selectMessage: "Sélectionnez les transactions à inclure dans le paiement",
      columns: {
        employeeName: "Nom de l'Employé",
        transactionType: "Type de Transaction",
        transactionDate: "Date de Transaction",
        client: "Client",
        workerName: "Nom du Travailleur",
        transactionNumber: "Numéro de Transaction",
        startDate: "Date de Début",
        endDate: "Date de Fin",
        grossMargin: "Montant de la Marge Brute",
        revenue: "Montant des Revenus",
        commissionSplit: "% de Partage de Commission",
        commission: "% de Commission",
        currency: "Devise",
        commissionAmount: "Montant de la Commission",
        invoiceARStatus: "Statut AR de Facture",
        permGuaranteeDate: "Date de Garantie Permanent",
        scheduledPaymentDate: "Date de Paiement Planifié",
        commissionRole: "Rôle de Commission",
        commissionRateDescription: "Description de la Commission",
        billingTransactionStatus: "Statut de transaction de facturation",
        invoiceNumber: "Numéro de facture",
        invoiceReleased: "Facture Libérée",
        invoiceDate: "Date de Facture",
        clientPaymentDate: "Date de Paiement Client",
        tierLevel: "Niveau du palier",
        commissionAccrualDate: "Date de Cumul de Commission",
        internalCompany: "Entreprise interne",
        commissionAdjustmentDescription: "Description de l'Ajustement de Commission"
      },
      noTransactions: {
        current: "Aucune transaction disponible pour le paiement",
        future: "Aucune transaction planifiée trouvée"
      }
    },
    actions: {
      preparePayout: "Préparer le Paiement",
      releaseCommission: "Libérer la Commission",
      viewHistory: "Voir l'Historique",
      resetFilter: "Réinitialiser le Filtre",
      loadTransactions: "Charger les Transactions",
      actionsPlaceholder: "Actions...",
      generateDraws: "Générer les Prélèvements et Ajustements Récurrents",
      generateDrawsConfirmTitle: "Générer les Prélèvements et Ajustements Récurrents",
      generateDrawsConfirmDescription: "Cela générera des transactions de prélèvement et d'ajustement récurrent pour {period}. Voulez-vous continuer ?",
      generateDrawsConfirmAction: "Continuer",
      generateDrawsConfirmCancel: "Annuler",
      proceed: "Continuer",
      deferToFuture: "Reporter au Futur",
      deferModalTitle: "Reporter au Futur",
      deferDateLabel: "Sélectionner une date",
      deferConfirm: "Confirmer le Report",
      deferCancel: "Annuler",
      finalizePeriod: "Finaliser la Période",
      finalizing: "Finalisation...",
      finalizePeriodConfirmTitle: "Finaliser la Période",
      finalizePeriodConfirmDescription: "Êtes-vous sûr de vouloir finaliser cette période ? Cette action est irréversible. Les transactions tardives seront déplacées vers la période suivante.",
      finalizePeriodConfirmAction: "Finaliser",
      finalizePeriodConfirmCancel: "Annuler",
      overdue: "En retard"
    },
    messages: {
      balanceError: "Erreur",
      balanceErrorDescription: "Échec du chargement des soldes d'ouverture.",
      refreshError: "Erreur",
      refreshErrorDescription: "Échec du rafraîchissement des transactions. Veuillez réessayer.",
      releaseSuccess: "Succès",
      releaseSuccessDescription: "Transaction(s) planifiée(s) avec succès.",
      releaseError: "Erreur",
      releaseErrorDescription: "Échec de la planification des commissions. Veuillez réessayer.",
      generateSuccess: "Succès",
      generateSuccessDescription: "Transaction(s) d'ajustement récurrent générée(s) avec succès.",
      generateError: "Erreur",
      generateErrorDescription: "Échec de la génération des ajustements récurrents. Veuillez réessayer.",
      finalizeSuccess: "Succès",
      finalizeSuccessDescription: "Période finalisée avec succès. Passage à la période suivante.",
      finalizeError: "Erreur",
      finalizeErrorDescription: "Échec de la finalisation de la période.",
      payoutSuccess: "Paiement Confirmé",
      payoutSuccessDescription: "paiement(s) d'utilisateur(s) confirmé(s) avec succès.",
      payoutError: "Erreur",
      payoutErrorDescription: "Échec de la confirmation du paiement. Veuillez réessayer.",
      usersError: "Erreur",
      usersErrorDescription: "Échec du chargement des utilisateurs de commission pour la liste déroulante ponctuelle.",
      deferSuccess: "Succès",
      deferSuccessDescription: "Transaction(s) reportée(s) avec succès.",
      deferError: "Erreur",
      deferErrorDescription: "Échec du report des transactions. Veuillez réessayer.",
      deferDateFuture: "La date de report doit être dans le futur."
    }
  },
  prepare: {
    title: "Préparer le Paiement des Commissions",
    backToDashboard: "Retour au Tableau de Bord",
    loading: "Initialisation...",
    noTransactions: {
      message: "Aucune transaction sélectionnée. Veuillez sélectionner des transactions depuis le tableau de bord.",
      returnButton: "Retourner au Tableau de Bord"
    },
    summary: {
      title: "Résumé du Paiement",
      description: "Paiement des Commissions",
      commissionSummary: {
        title: "Résumé des Commissions",
        openingBalance: "Solde d'Ouverture",
        standardCommissions: "Commissions Standards",
        permanentPlacements: "Commissions de Placement Permanent",
        draws: "Prélèvements de la Période",
        adjustments: "Ajustements de la Période",
        netChange: "Variation Nette de la Période",
        drawsPayable: "Prélèvements à Payer de la Période",
        netPayable: "Net à Payer de la Période",
        closingBalance: "Solde de Clôture (à reporter)"
      },
      userBreakdown: {
        title: "Répartition par Utilisateur",
        columns: {
          employeeName: "Nom de l'Employé",
          role: "Rôle",
          standardCommissions: "Commissions Standards",
          permanentPlacements: "Placements Permanents",
          totalAmount: "Commissions Gagnées",
          openingBalance: "Solde d'Ouverture",
          draws: "Tirages",
          adjustments: "Ajustements",
          netChange: "Variation Nette",
          netPayable: "Net à Payer",
          closingBalance: "Solde de Clôture"
        }
      }
    },
    payoutDetails: {
      title: "Détails du Paiement",
      payoutType: "Type de Paiement",
      scheduledPayout: "Paiement Programmé",
      currentPeriodEnding: "Fin de la Période en Cours :",
      scheduledPayoutDate: "Paiement Prévu :",
      adhocPayout: "Paiement Ponctuel",
      statementPeriod: "Période de Relevé",
      selectEmployee: "Sélectionner un Employé",
      selectEmployeePlaceholder: "Choisir un employé...",
      payoutDate: "Date de Paiement",
      notes: "Notes (Optionnel)",
      notesPlaceholder: "Ajoutez des notes supplémentaires sur ce paiement..."
    },
    actions: {
      confirm: "Confirmer le Paiement",
      cancel: "Annuler",
      resetFilter: "Réinitialiser le Filtre"
    }
  },
  history: {
    title: "Historique des Paiements de Commission",
    backToDashboard: "Retour au Tableau de Bord",
    loading: "Initialisation...",
    payouts: {
      title: "Paiements Historiques",
      description: "Consulter et accéder aux paiements de commission précédents",
      selectYear: "Sélectionner l'Année",
      columns: {
        id: "Identifiant du Lot de Paiement",
        payoutDate: "Date de Paiement",
        processedDate: "Date de Traitement",
        numOfPayees: "# Payés",
        openingBalance: "Solde d'Ouverture",
        standard: "Standard",
        permanent: "Permanent",
        commissionsEarned: "Commissions Gagnées",
        draws: "Prélèvements",
        adjustments: "Ajustements",
        netChange: "Variation Nette",
        netPayable: "Montant Net Payable",
        closingBalance: "Solde de Clôture",
        notes: "Notes",
        payoutType: "Type de Paiement"
      },
      payoutTypes: {
        scheduled: "Planifié",
        adhoc: "Ponctuel"
      },
      loadingMessage: "Chargement...",
      errorMessage: "Échec du chargement de l'historique des paiements"
    },
    actions: {
      resetFilter: "Réinitialiser le Filtre"
    }
  },
  summary: {
    title: "Résumé du Paiement des Commissions",
    backToHistory: "Retour à l'Historique"
  }
}

// Dictionary map
export const dictionaries = {
  "en-CA": enCA,
  "fr-CA": frCA,
}

export type Locale = keyof typeof dictionaries
